import { AccessControl, AccessControlRes } from '@/types/common/accessControl';
import { createPermissionsObject } from '@/utils/permissionsUtil';
import { useAccount, useMsal } from '@azure/msal-react';
import { createContext, ReactNode, useEffect, useState } from 'react';
import { getAccessControl, getByLoginName, getCurrentUser, getUserProfileForCurrentUser } from '../services/user/User';
import { initialUserValues, User, UserProfileProperties } from '../types/common/user';
import { clearOriginalUser, getOriginalUser, storeOriginalUser } from '../utils/cookieEncryption';
import { hasCookieSession } from '../services/auth/cookieSession';
import { useApiClient } from './ApiContext';

type CurrentUserContextType = {
    currentUser: User;
    originalUser: User;
    accessControl: AccessControl;
    profileProperties: UserProfileProperties | null;
    isOnBehalfOf: boolean;
    isLoading: boolean;
    /**
     * Switch the current user to another login ("on behalf of"). Resolves true when the
     * switch happened, false when the login could not be loaded (not found, API error, no
     * account) - in which case the current user is left unchanged. Never rejects.
     */
    setCurrentUserByLoginName: (loginName: string) => Promise<boolean>;
    resetToOriginalUser: () => void;
}
const CurrentUserContext = createContext<CurrentUserContextType>({
    currentUser: initialUserValues,
    originalUser: initialUserValues,
    accessControl: new AccessControl(),
    profileProperties: null,
    isOnBehalfOf: false,
    isLoading: true,
    setCurrentUserByLoginName: async () => false,
    resetToOriginalUser: () => { }
});
const CurrentUserProvider = ({ children }: { children: ReactNode }) => {
    const [currentUser, setCurrentUser] = useState<User>(initialUserValues);
    const [originalUser, setOriginalUser] = useState<User>(initialUserValues);
    const [accessControl, setAccessControl] = useState<AccessControl>(new AccessControl());
    const [profileProperties, setProfileProperties] = useState<UserProfileProperties | null>(null);
    const [isOnBehalfOf, setIsOnBehalfOf] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    // Embedded hosts can be authenticated by a cookie session instead of MSAL, in which case
    // there is no account but `me` still resolves. undefined while the probe is in flight;
    // always false outside embedded routes, so standalone behavior is unchanged.
    const [cookieSession, setCookieSession] = useState<boolean | undefined>(undefined);
    const api = useApiClient();
    const { accounts } = useMsal();
    const account = useAccount(accounts[0] || {});

    useEffect(() => {
        let cancelled = false;
        hasCookieSession().then((ok) => {
            if (!cancelled) setCookieSession(ok);
        });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        // Don't conclude nobody is signed in until the cookie probe has answered
        if (cookieSession === undefined) return;

        // Only fetch user data if we have an authenticated account or a host cookie session
        if ((account && account.homeAccountId) || cookieSession) {
            setIsLoading(true);

            // Check if we have the original user in cookie
            const storedOriginalUser = getOriginalUser();

            getCurrentUser(api)
                .then((response) => {
                    const user = response.data.value[0];
                    //remove domain part 
                    if (user.LoginName) {
                        const loginNameParts = user.LoginName.split('\\');
                        user.LoginName = loginNameParts[loginNameParts.length - 1];
                    }
                    setCurrentUser(user);

                    // If no stored original user, this is the first load - store it
                    if (!storedOriginalUser || storedOriginalUser.Id === 0) {
                        setOriginalUser(user);
                        storeOriginalUser(user);
                    } else {
                        // Use the stored original user
                        setOriginalUser(storedOriginalUser);
                    }

                    setIsLoading(false);
                })
                .catch((err) => {
                    console.error("Error fetching current user:", err);
                    setIsLoading(false);
                });
            getAccessControl(api)
                .then((res) => {
                    setAccessControl(new AccessControl(createPermissionsObject(res.data as AccessControlRes[])));
                })
                .catch((err) => {
                    console.error(err)
                    setAccessControl(new AccessControl());
                })
        } else if (!account) {
            // Reset user data when not authenticated and clear cookie
            console.log('No authenticated account, resetting user data and clearing cookie');
            setCurrentUser(initialUserValues);
            setOriginalUser(initialUserValues);
            setIsOnBehalfOf(false);
            setIsLoading(false);
            clearOriginalUser();
        }
    }, [api, account?.homeAccountId, account, cookieSession]); // Listen to API client, account and cookie session changes
    useEffect(() => {
        if (currentUser.LoginName && currentUser.LoginName !== 'undefined') {
            getUserProfileForCurrentUser(currentUser.LoginName, api)
                .then((response) => {
                    setProfileProperties(response.data?.profileFound ? response.data.properties : null);
                })
                // Profile properties are supplementary - the app works without
                // them, so a SharePoint profile failure must not break sign in.
                .catch((err) => {
                    console.error("Error fetching user profile:", err);
                    setProfileProperties(null);
                })
        }
    }, [currentUser, api])
    const setCurrentUserByLoginName = async (loginName: string): Promise<boolean> => {
        if (!account) {
            console.error("Cannot switch user: no authenticated account available");
            return false;
        }

        try {
            setIsLoading(true);
            const response = await getByLoginName(loginName, api);
            const match = response.data?.value?.[0];
            if (!match) {
                console.warn(`No user found for login name "${loginName}"; keeping the current user.`);
                return false;
            }
            match.LoginName = loginName; // Ensure the login name is set to the one we searched for
            setCurrentUser(match);
            setIsOnBehalfOf(loginName !== originalUser.LoginName);
            return true;
        } catch (err) {
            // A failed lookup must never take the page down: log it, keep the current user
            // and let the caller tell the person what happened.
            console.error("Error fetching user by login name:", err);
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    const resetToOriginalUser = () => {
        setCurrentUser(originalUser);
        setIsOnBehalfOf(false);
    };

    return (
        <CurrentUserContext.Provider value={{
            currentUser,
            originalUser,
            accessControl,
            profileProperties,
            isOnBehalfOf,
            isLoading,
            setCurrentUserByLoginName,
            resetToOriginalUser
        }}>
            {children}
        </CurrentUserContext.Provider>
    )
}
export { CurrentUserContext, CurrentUserProvider };
